import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Cascader, Dropdown, message, theme } from 'antd-v5';
import { getVendorItemCollections, getVendorItemFamilies, getVendorItemMaterials } from '../../../api/vendors';

const { useToken } = theme;

const VendorItemFiltersCascader = props => {
  const {
    setOpen,
    selectedFilters,
    setSelectedFilters,
    collectionsOptions,
    familiesOptions,
    materialsOptions,
    hasCollection,
    hasFamily,
    hasMaterial,
  } = props;
  const { token } = useToken();
  const [cascaderOpen, setCascaderOpen] = useState(false);

  const contentStyle = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
    padding: '8px',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    // Reserve room for the Cascader's floating panel (capped at 180px per
    // column by antd) so Apply/Cancel stay visible below it while open.
    minHeight: cascaderOpen ? '280px' : undefined,
  };

  const [cascaderValue, setCascaderValue] = useState([]);

  const options = useMemo(() => {
    const opts = [];
    if (hasCollection && collectionsOptions.length > 0) {
      opts.push({ value: 'collections', label: 'Collection', children: collectionsOptions });
    }
    if (hasFamily && familiesOptions.length > 0) {
      opts.push({ value: 'families', label: 'Family', children: familiesOptions });
    }
    if (hasMaterial && materialsOptions.length > 0) {
      opts.push({ value: 'materials', label: 'Material', children: materialsOptions });
    }
    return opts;
  }, [collectionsOptions, familiesOptions, materialsOptions, hasCollection, hasFamily, hasMaterial]);

  const syncCascaderFromFilters = useCallback(() => {
    const newValue = [];
    const { collections = [], families = [], materials = [] } = selectedFilters;

    if (hasCollection) {
      if (collectionsOptions.length > 0 && collections.length === collectionsOptions.length) {
        newValue.push(['collections']);
      } else {
        newValue.push(...collections.map(item => ['collections', item]));
      }
    }
    if (hasFamily) {
      if (familiesOptions.length > 0 && families.length === familiesOptions.length) {
        newValue.push(['families']);
      } else {
        newValue.push(...families.map(item => ['families', item]));
      }
    }
    if (hasMaterial) {
      if (materialsOptions.length > 0 && materials.length === materialsOptions.length) {
        newValue.push(['materials']);
      } else {
        newValue.push(...materials.map(item => ['materials', item]));
      }
    }
    setCascaderValue(newValue);
  }, [selectedFilters, collectionsOptions, familiesOptions, materialsOptions, hasCollection, hasFamily, hasMaterial]);

  useEffect(() => {
    syncCascaderFromFilters();
  }, [syncCascaderFromFilters]);

  const handleClickApply = useCallback(() => {
    const newFilters = { collections: [], families: [], materials: [] };

    cascaderValue.forEach(item => {
      const category = item[0];
      const value = item[1];
      if (category === 'collections') {
        if (value === undefined) {
          newFilters.collections = collectionsOptions.map(o => o.value);
        } else {
          newFilters.collections.push(value);
        }
      } else if (category === 'families') {
        if (value === undefined) {
          newFilters.families = familiesOptions.map(o => o.value);
        } else {
          newFilters.families.push(value);
        }
      } else if (category === 'materials') {
        if (value === undefined) {
          newFilters.materials = materialsOptions.map(o => o.value);
        } else {
          newFilters.materials.push(value);
        }
      }
    });

    setSelectedFilters(newFilters);
    setOpen(false);
    message.success('Filters have been applied');
  }, [cascaderValue, collectionsOptions, familiesOptions, materialsOptions, setSelectedFilters, setOpen]);

  const handleClickCancel = useCallback(() => {
    syncCascaderFromFilters();
    setOpen(false);
    message.info('Changes have been discarded');
  }, [syncCascaderFromFilters, setOpen]);

  const handleClickClear = useCallback(() => {
    setSelectedFilters({ collections: [], families: [], materials: [] });
    setOpen(false);
    message.info('Filters have been cleared');
  }, [setSelectedFilters, setOpen]);

  return (
    <div style={contentStyle}>
      <style>{`.vendor-item-filters-cascader .ant5-cascader-menu-item-content,
.vendor-item-filters-cascader .ant5-select-selection-item,
.vendor-item-filters-popup .ant5-cascader-menu-item-content { text-transform: capitalize; }`}</style>
      <Cascader
        className="vendor-item-filters-cascader"
        popupClassName="vendor-item-filters-popup"
        value={cascaderValue}
        onChange={setCascaderValue}
        options={options}
        placeholder="Select filters"
        maxTagCount={10}
        style={{ width: '280px', marginBottom: '12px' }}
        onClear={handleClickClear}
        open={cascaderOpen}
        onDropdownVisibleChange={setCascaderOpen}
        showSearch
        allowClear
        multiple
      />
      <div style={{ display: 'flex', gap: '12px', marginTop: 'auto' }}>
        <Button onClick={handleClickCancel} block>
          Cancel
        </Button>
        <Button type="primary" onClick={handleClickApply} block>
          Apply
        </Button>
      </div>
    </div>
  );
};

const VendorItemFilters = props => {
  const {
    open,
    setOpen,
    vendorId,
    hasCollection,
    hasFamily,
    hasMaterial,
    selectedFilters,
    setSelectedFilters,
    children,
  } = props;

  const [collectionsOptions, setCollectionsOptions] = useState([]);
  const [familiesOptions, setFamiliesOptions] = useState([]);
  const [materialsOptions, setMaterialsOptions] = useState([]);

  useEffect(() => {
    if (!vendorId) return;

    if (hasCollection) {
      getVendorItemCollections([vendorId])
        .then(response => {
          setCollectionsOptions((response?.data || []).map(item => ({ value: item.name, label: item.name })));
        })
        .catch(() => setCollectionsOptions([]));
    }

    if (hasFamily) {
      getVendorItemFamilies([vendorId])
        .then(response => {
          setFamiliesOptions((response?.data || []).map(item => ({ value: item.name, label: item.name })));
        })
        .catch(() => setFamiliesOptions([]));
    }

    if (hasMaterial) {
      getVendorItemMaterials([vendorId])
        .then(response => {
          setMaterialsOptions((response?.data || []).map(item => ({ value: item.name, label: item.name })));
        })
        .catch(() => setMaterialsOptions([]));
    }
  }, [vendorId, hasCollection, hasFamily, hasMaterial]);

  const handleOpenFilter = useCallback(() => {
    setOpen(prev => !prev);
  }, [setOpen]);

  return (
    <Dropdown
      placement="bottomLeft"
      trigger={['click']}
      onOpenChange={handleOpenFilter}
      open={open}
      destroyPopupOnHide
      // eslint-disable-next-line react/no-unstable-nested-components
      dropdownRender={() => (
        <VendorItemFiltersCascader
          setOpen={setOpen}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          collectionsOptions={collectionsOptions}
          familiesOptions={familiesOptions}
          materialsOptions={materialsOptions}
          hasCollection={hasCollection}
          hasFamily={hasFamily}
          hasMaterial={hasMaterial}
        />
      )}
    >
      {children}
    </Dropdown>
  );
};

export default VendorItemFilters;
